import dayjs from "dayjs";
import { Button, Dialog } from "@mui/material";
import { useScale, btnDelay } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import i18n from "features/shared/i18n";
import { ParentPieceReport, ParentReportTarget } from "features/player/types";
import {
  reportStatus,
  reportReasonKey,
  reportTargetName,
  reportTargetLabelKey,
} from "./ParentPieceReportRow";

export const PieceReportDetailModal = ({
  report,
  onClose,
  onOpenPiece,
}: {
  report: ParentPieceReport;
  onClose: () => void;
  /** 通報した先（ピース or アプリ）の詳細をひらく */
  onOpenPiece: () => void;
}) => {
  const { scale } = useScale();
  const status = reportStatus(report.status);
  const isProject = report.target === ParentReportTarget.PROJECT;

  return (
    <Dialog
      fullScreen
      open={true}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el justify-start w-[760px] max-h-[760px] relative overflow-hidden border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el absolute top-0 right-4 w-14 h-14 z-[110]">
              <ParentCloseBtn close={onClose} />
            </div>
            <div className="flex-col-el justify-start w-full flex-1 min-h-0 relative p-6 rounded-[8px] bg-white overflow-y-auto visible-scrollbar">
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline">
                <span className="material-symbols-outlined !text-[45px] text-red-100">flag</span>
                <h2 className="text-[27px] text-textcolor-black">
                  {i18n.t("MSG_PARENT_PAGE_PLAYER_REPORTS")}
                </h2>
              </div>

              <div className="flex-col-el gap-3">
                <div className="flex-row-el items-center gap-2 whitespace-nowrap">
                  <span className="material-symbols-outlined text-[24px] text-red-100">
                    {isProject ? "folder" : "extension"}
                  </span>
                  <span className="text-[24px] text-textcolor-black">
                    {reportTargetName(report)}
                  </span>
                  <span className="text-[19px] text-gray2-100">
                    {i18n.t(reportTargetLabelKey(report.target))}
                  </span>
                  <span
                    className={`text-[19px] text-white px-3 py-1 rounded-full ${status.className}`}
                  >
                    {i18n.t(status.labelKey)}
                  </span>
                </div>

                {report.comment_content && (
                  <p className="text-[21px] text-textcolor-black whitespace-pre-wrap break-all p-4 rounded-[12px] bg-gray2-20">
                    {report.comment_content}
                  </p>
                )}

                <div className="flex-col-el gap-1">
                  <span className="text-[19px] text-gray2-100">
                    {i18n.t("MSG_PARENT_PAGE_PLAYER_REPORT_REASON")}
                  </span>
                  <span className="text-[21px] text-textcolor-black">
                    {i18n.t(reportReasonKey(report.reason))}
                  </span>
                </div>

                {report.detail && (
                  <div className="flex-col-el gap-1">
                    <span className="text-[19px] text-gray2-100">
                      {i18n.t("MSG_PARENT_PAGE_PLAYER_REPORT_DETAIL")}
                    </span>
                    <p className="text-[21px] text-textcolor-black whitespace-pre-wrap break-all">
                      {report.detail}
                    </p>
                  </div>
                )}

                <div className="flex-row-el items-center justify-between gap-4 text-[19px] text-gray2-100 pt-2 border-t border-outline">
                  {dayjs(report.created_at).format("YYYY-MM-DD HH:mm")}
                  <Button
                    variant="outlined"
                    onClick={() => {
                      btnDelay(onOpenPiece);
                    }}
                  >
                    {i18n.t(
                      isProject
                        ? "MSG_PARENT_PAGE_PLAYER_PROJECT_OPEN"
                        : "MSG_PARENT_PAGE_PLAYER_PIECE_OPEN",
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
